import litvik_logo from "./img/litvik_logo.jpg";
import vasantham_logo from "./img/vasantham-logo.jpg";
import { useState, useEffect, useRef } from "react";
import './Login.css';
import { useCookies } from 'react-cookie';
import { AiOutlineHome } from "react-icons/ai";
import { getBgImageLogo, submitLogin } from './services/LoginServices';
import { displayError } from './helpers';
import Swal from "sweetalert2";
import { clearAuthSession, getApiBaseUrl } from './services/serviceconfig';
import {
  AUTH_COOKIE_NAMES,
  consumeSessionExpiredNotice,
  hasBrowserSession,
  isAuthenticated,
  isIdleExpired,
  markSessionActive,
  SESSION_EXPIRED_MESSAGE,
} from './authSession';
import {
  LOGIN_COMPANY_DISPLAY_NAME,
  resolveLoginCompany,
  getCompanyMediaUrl,
} from './utils/loginCompany';

const FEATURES = [
  { title: 'Marketing Management', desc: 'Customer, project, and sales order workflows' },
  { title: 'Planning Management', desc: 'Schedule, production, and dispatch coordination' },
  { title: 'Accounting Management', desc: 'Invoice, receipts, payments, banking, adjustments' },
  { title: 'Fleet Management', desc: 'Trucks, pumps, drivers, diesel, and fleet reports' },
  { title: 'Quality Control', desc: 'Material analysis, design mix, and QC reports' },
  { title: 'Inventory Management', desc: 'Vendor, purchase order, GRN, delivery, and stock' },
];

function Login() {
  const apiBaseUrl = getApiBaseUrl();
  const [inputs, setInputs] = useState({});
  const [tfa, setTfa] = useState({
    secret_code: '',
    qrcode: '',
    otp: '',
  });
  const [error, setError] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTfa, setShowTfa] = useState(false);
  const [showQrCode, setShowQrCode] = useState(true);
  const [cookies, setCookie, removeCookie] = useCookies(['myToken']);
  const [, setName] = useCookies(['myName']);
  const [, setMyUserId] = useCookies(['myUserId']);
  const [, setCategory] = useCookies(['myCategory']);
  const [, setSuperuser] = useCookies(['mySuperuser']);
  const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
  const [companyLogo, setCompanyLogo] = useState(vasantham_logo);
  const [companyDisplayName, setCompanyDisplayName] = useState(LOGIN_COMPANY_DISPLAY_NAME);
  const gridRef = useRef(null);

  const options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  function success(pos) {
    const crd = pos.coords;
    setPosition({
      latitude: crd.latitude,
      longitude: crd.longitude,
    });
  }

  function errors(err) {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  useEffect(() => {
    getBgImageLogo(cookies)
      .then((response) => {
        const company = resolveLoginCompany(response?.company_list);
        if (company) {
          if (company.name) {
            setCompanyDisplayName(company.name);
          }
          const logoUrl = getCompanyMediaUrl(apiBaseUrl, company.logo);
          if (logoUrl) {
            setCompanyLogo(logoUrl);
          }
        }
      })
      .catch((err) => {
        console.log(err, "getBgImageLogo failed");
      });
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return undefined;
    }
    const loginCard = grid.querySelector('.login-card--form');
    if (!loginCard) {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 1100px)');

    const syncColumnHeight = () => {
      if (mediaQuery.matches) {
        grid.classList.remove('is-desktop');
        grid.style.removeProperty('--login-col-height');
        return;
      }
      grid.classList.add('is-desktop');
      grid.style.setProperty('--login-col-height', `${loginCard.offsetHeight}px`);
    };

    syncColumnHeight();
    const observer = new ResizeObserver(syncColumnHeight);
    observer.observe(loginCard);
    mediaQuery.addEventListener('change', syncColumnHeight);
    window.addEventListener('resize', syncColumnHeight);
    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', syncColumnHeight);
      window.removeEventListener('resize', syncColumnHeight);
    };
  }, [showTfa, companyDisplayName]);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if (name === 'username') {
      if (!value.trim()) {
        setError((values) => ({ ...values, [name]: "User Name must not be empty" }));
        setIsSubmitting(false);
      } else {
        setError((values) => ({ ...values, [name]: '' }));
        setIsSubmitting(true);
      }
      setInputs((values) => ({ ...values, [name]: value }));
    } else if (name === 'password') {
      if (!value.trim()) {
        setError((values) => ({ ...values, [name]: "Password must not be empty" }));
        setIsSubmitting(false);
      } else {
        setError((values) => ({ ...values, [name]: '' }));
        setIsSubmitting(true);
      }
      setInputs((values) => ({ ...values, [name]: value }));
    } else if (name === 'otp') {
      if (!value.trim()) {
        setError((values) => ({ ...values, [name]: "OTP must not be empty" }));
        setIsSubmitting(false);
      } else {
        setError((values) => ({ ...values, [name]: '' }));
        setIsSubmitting(true);
      }
      setTfa((values) => ({ ...values, [name]: value }));
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            navigator.geolocation.getCurrentPosition(success);
          } else if (result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(success, errors, options);
          }
        });
    } else {
      Swal.fire("Sorry Not available!", "", "info");
    }

  }, []);

  useEffect(() => {
    const showedExpired = consumeSessionExpiredNotice();
    if (showedExpired) {
      Swal.fire('Session Expired', SESSION_EXPIRED_MESSAGE, 'info');
    }

    if (!cookies?.myToken) {
      return;
    }

    if (!hasBrowserSession() || isIdleExpired()) {
      clearAuthSession();
      AUTH_COOKIE_NAMES.forEach((cookieName) => {
        removeCookie(cookieName, { path: '/' });
      });
      if (!showedExpired) {
        Swal.fire('Session Expired', SESSION_EXPIRED_MESSAGE, 'info');
      }
      return;
    }

    if (isAuthenticated(cookies)) {
      window.location.href = '/Home/schedule';
    }
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    if ((Object.values(error).every((x) => !x)) && isSubmitting) {
      submitLogin(inputs.username, inputs.password, tfa.otp, position.latitude, position.longitude)
        .then((login_response) => {
          if (login_response.tfa_enabled === false) {
            setTfa((values) => ({
              ...values,
              secret_code: login_response.otp_base32,
              qrcode: login_response.img_str,
            }));
            setInputs((values) => ({ ...values, username: login_response.username }));
            setShowQrCode(true);
            setShowTfa(true);
          } else if (login_response.tfa_enabled === true && login_response.status === 'have to verify otp') {
            setShowQrCode(false);
            setShowTfa(true);
          } else if (login_response.token) {
            // Keep the auth token off insecure connections and out of
            // cross-site requests. `secure` is conditional so that plain-HTTP
            // local development (npm start) can still store the cookie.
            const cookieOptions = {
              path: '/',
              secure: window.location.protocol === 'https:',
              sameSite: 'strict',
            };
            setCookie('myToken', login_response.token, cookieOptions);
            setName('myName', login_response.name, cookieOptions);
            setMyUserId('myUserId', login_response.user_id, cookieOptions);

            if (!login_response.is_superuser) {
              setCategory('myCategory', login_response.category, cookieOptions);
            } else {
              setSuperuser('mySuperuser', login_response.is_superuser, cookieOptions);
            }
            markSessionActive();
            window.location.href = '/Home/schedule';
          }
        })
        .catch((loginError) => {
          const errorData = loginError?.response?.data || loginError?.message || "Unable to reach the server.";
          displayError(errorData, "Authentication Failed.");
        });
    }
  };

  return (
    <div className="login-page">
      <header className="login-page__brand">
        <img src={litvik_logo} alt="Litvik" className="login-page__brand-logo" />
        <div className="login-page__brand-text">
          <h1 className="login-page__brand-title">Litvik Software Labs Pvt. Ltd.</h1>
          <p className="login-page__brand-kicker">Ready Mix Concrete ERP</p>
        </div>
      </header>

      {!showTfa ? (
        <div className="login-page__grid" ref={gridRef}>
          <aside className="login-card login-card--info">
            <span className="login-card__eyebrow">Support</span>
            <h2 className="login-card__title">Contact Details</h2>
            <p className="login-card__lead">
              Get in touch with us for questions about your plant operations, modules, or implementation.
            </p>
            <div className="login-contact-item">
              <div className="login-contact-item__icon">
                <AiOutlineHome size={22} />
              </div>
              <div>
                <h3 className="login-contact-item__title">Office Address</h3>
                <p className="login-contact-item__text">
                  Plot No.1, Second Floor,
                  <br />
                  Thiruvalluvar Cross Street, Madambakkam,
                  <br />
                  Chennai, Tamil Nadu, India - 600126
                  <br />
                  Mobile: 95516 14121
                  <br />
                  sales@litvik.in
                </p>
              </div>
            </div>
          </aside>

          <main className="login-card login-card--form">
            <div className="login-card__avatar-wrap">
              <img
                src={companyLogo}
                alt={companyDisplayName}
                className="login-card__avatar"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = vasantham_logo;
                }}
              />
            </div>
            <h2 className="login-card__company-name">{companyDisplayName}</h2>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="login-form__group">
                <label className="login-form__label" htmlFor="username">User Name</label>
                <div className="login-form__field">
                  <span className="login-form__field-icon"><i className="fa fa-user" /></span>
                  <input
                    id="username"
                    className="login-form__input"
                    type="text"
                    name="username"
                    required
                    onChange={handleChange}
                    value={inputs.username || ""}
                    placeholder="Enter username"
                  />
                </div>
                {error.username ? <p className="login-form__error">{error.username}</p> : null}
              </div>

              <div className="login-form__group">
                <label className="login-form__label" htmlFor="password">Password</label>
                <div className="login-form__field">
                  <span className="login-form__field-icon"><i className="fa fa-lock" /></span>
                  <input
                    id="password"
                    className="login-form__input"
                    type="password"
                    name="password"
                    required
                    onChange={handleChange}
                    value={inputs.password || ""}
                    placeholder="Enter password"
                  />
                </div>
                {error.password ? <p className="login-form__error">{error.password}</p> : null}
              </div>

              <input className="login-form__submit" type="submit" value="Log In" />
            </form>
          </main>

          <aside className="login-card login-card--features">
            <span className="login-card__eyebrow">Modules</span>
            <h2 className="login-card__title">ERP Features</h2>
            <p className="login-card__lead">
              Litvik ERP simplifies ready-mix concrete operations with integrated daily workflows.
            </p>
            <ul className="login-feature-list">
              {FEATURES.map((feature) => (
                <li key={feature.title} className="login-feature-list__item">
                  <span className="login-feature-list__bullet" />
                  <div>
                    <span className="login-feature-list__title">{feature.title}</span>
                    <span className="login-feature-list__desc">{feature.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      ) : (
        <div className="login-page__tfa">
          <div className="login-card login-tfa-card">
            <h2 className="login-tfa-card__title">Two-Factor Authentication</h2>
            {showQrCode ? (
              <>
                <p className="login-tfa-card__hint">Scan the QR code with your authenticator app</p>
                <img
                  src={`data:image/jpeg;base64,${tfa.qrcode}`}
                  alt="2FA QR code"
                  className="login-tfa-card__qr"
                />
              </>
            ) : (
              <p className="login-tfa-card__hint">Enter the one-time password from your authenticator app</p>
            )}
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="login-form__group">
                <label className="login-form__label" htmlFor="otp">One-Time Password</label>
                <input
                  id="otp"
                  className="login-form__input"
                  style={{ paddingLeft: '14px' }}
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  onChange={handleChange}
                  value={tfa.otp || ""}
                  required
                />
                {error.otp ? <p className="login-form__error">{error.otp}</p> : null}
              </div>
              <button type="submit" className="login-tfa-card__submit">Verify &amp; Activate</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
